{
  "_doc": "What the in-house agent knows about the portal UI: pages, anchors it can point at, forms it may prefill, dialogs it may open, tours. Source of truth — vendored into the portal by scripts/sync_portal_ui_catalog.py, checked by services/client-portal/scripts/check-agent-contracts.mjs. Texts are written for the agent (English); what users read comes from i18n. See features/docs/technical/portal-agent-native.md.",
  "version": 1,
  "pages": {
    "knowledge.base": {
      "route": "/knowledge-base",
      "area": "knowledge",
      "purpose": "All knowledge bases (folders) of the organisation and the files, websites and cloud sources inside them. Left: the knowledge bases; right: the content of the selected one.",
      "tasks": ["create a knowledge base", "upload files", "add websites", "connect a cloud drive", "rename or delete a knowledge base"],
      "actions": ["knowledge.bucket.list", "knowledge.bucket.entries", "knowledge.bucket.update"]
    },
    "account.settings": {
      "route": "/settings",
      "area": "account",
      "purpose": "The signed-in user's own account: profile, language, password and two-factor sign-in; further tabs for AI usage and limits, the portal assistant's access, and connections (connect a Figma account so the course editor can bring in Figma frames; admins can also connect one for the whole organisation).",
      "tasks": ["change the portal language", "update the own profile", "connect Figma for the course editor"]
    },
    "academy.home": {
      "route": "/academy",
      "area": "account",
      "purpose": "The Academy: learning and help centre for every portal user. Short lessons (explainer video, summary, mini quiz) grouped in eight topics, the user's own progress, the recommended next lesson, news and the three certificates of the AlphaLearn licence. ?view=hints lists every portal hint with its lesson, ?q= searches.",
      "tasks": ["learn how a feature works", "continue learning", "see all portal hints", "check the own learning progress"],
      "actions": ["account.academy.progress"]
    },
    "academy.topic": {
      "route": "/academy/topics/:topicId",
      "area": "account",
      "tab_param": "topicId",
      "tabs": ["getting-started", "agents", "knowledge", "widget", "lecturers", "analytics", "administration", "expert"],
      "purpose": "One Academy topic with its lessons in order, the user's progress and the certificate the topic counts for.",
      "tasks": ["open the lessons of a topic"]
    },
    "academy.lesson": {
      "route": "/academy/lessons/:lessonId",
      "area": "account",
      "purpose": "One Academy lesson: explainer video (for lessons that belong to a portal hint, the hint's film), short summary, key points, mini quiz with instant feedback, a button that opens the feature in the portal, and the next lesson. Lesson ids are listed on the topic pages.",
      "tasks": ["explain a feature with a short lesson", "try the feature in the portal"]
    },
    "academy.certificate": {
      "route": "/academy/certificates/:certificateId",
      "area": "account",
      "tab_param": "certificateId",
      "tabs": ["essentials", "professional", "expert"],
      "purpose": "One certificate of the AlphaLearn licence: requirements (previous certificate, all lessons of its topics) and the final test, which the user takes themselves.",
      "tasks": ["see what is missing for a certificate"],
      "actions": ["account.academy.progress"]
    },
    "agents.list": {
      "route": "/tutors",
      "area": "agents",
      "purpose": "Overview of all AI agents as tiles, grouped by template. Open an agent's configuration, try it in the chat preview, embed it on a website or create a new one.",
      "tasks": ["create an agent", "open an agent", "try an agent in the chat", "get the embed code"],
      "actions": ["agent.list", "agent.templates.creatable"]
    },
    "agents.placements": {
      "route": "/tutors/placements",
      "area": "lms",
      "purpose": "Where agents are placed in connected learning platforms (Moodle and ILIAS courses and activities) — assign an agent per course or activity.",
      "tasks": ["place an agent in a course", "see which agent runs where"]
    },
    "lms.registrations": {
      "route": "/integrations/lti",
      "area": "lms",
      "purpose": "LTI 1.3 connections to learning platforms (Moodle, ILIAS, Canvas, Brightspace): register a platform, create a one-time registration link, pause or disconnect a connection.",
      "tasks": ["connect a learning platform via LTI", "pause or disconnect a connection"]
    },
    "agent.general": {
      "route": "/tutors/:tutorId/general",
      "area": "agents",
      "params": { "tutorId": "bot" },
      "purpose": "Basic data of one agent: name, description, language, chat styling and the chat widget's onboarding tour.",
      "tasks": ["rename an agent", "change the agent's description", "configure the widget tour"],
      "actions": ["agent.get", "agent.update"]
    },
    "agent.ai-settings": {
      "route": "/tutors/:tutorId/ai-settings/:section",
      "area": "agents",
      "params": { "tutorId": "bot" },
      "tab_param": "section",
      "tabs": ["basis_persona", "model_limits", "guardrails", "faq", "integration"],
      "purpose": "AI configuration of one agent. Sections: basis_persona (name, persona, style, language), model_limits (AI provider and model picker — anchor setting.ai_provider — and usage quotas), guardrails (safety rules), faq (the agent's FAQ), integration (embedding). Every row is one setting, addressable as setting.<variable_name>.",
      "tasks": ["change the language model", "change the persona or answer style", "set usage limits", "maintain the agent's FAQ"],
      "actions": ["agent.settings.get", "agent.settings.update"]
    },
    "agent.knowledge": {
      "route": "/tutors/:tutorId/knowledge/:tab",
      "area": "knowledge",
      "params": { "tutorId": "bot" },
      "tab_param": "tab",
      "tabs": ["base", "usage"],
      "purpose": "Knowledge of one agent, two tabs. 'base' = „Wissensbasis“ (Knowledge base): which knowledge-base folders the agent answers from. 'usage' = „Quellen & Suche“ (Sources & search): how it uses them — knowledge mode (setting.knowledge_mode), source citations, web search.",
      "tasks": ["link a knowledge base to an agent (tab base)", "limit the agent to its knowledge base (tab usage)"],
      "actions": ["agent.settings.get", "agent.settings.update", "knowledge.bucket.list"]
    },
    "agent.learning": {
      "route": "/tutors/:tutorId/learning/:tab",
      "area": "agents",
      "params": { "tutorId": "bot" },
      "tab_param": "tab",
      "tabs": ["didaktik", "dozent", "functions", "learner_profile", "curriculum"],
      "purpose": "How one agent teaches: didactics, the linked dozent (Alpha Twin), learning-interface functions, learner profile and curriculum.",
      "tasks": ["switch exercise or debate mode on", "link a dozent", "configure the learner profile"],
      "actions": ["agent.settings.get", "agent.settings.update"]
    },
    "agent.setup": {
      "route": "/tutors/:tutorId/setup/:tab",
      "area": "agents",
      "params": { "tutorId": "bot" },
      "tab_param": "tab",
      "tabs": ["learnings", "skills", "mcp", "moodle", "guardrails"],
      "purpose": "Behaviour and capabilities of one agent: agent learnings, skills, MCP connectors, Moodle actions, rules and limits (guardrails).",
      "tasks": ["add a guardrail", "enable a skill for this agent", "connect an MCP server to this agent"]
    },
    "agent.access": {
      "route": "/tutors/:tutorId/access",
      "area": "agents",
      "params": { "tutorId": "bot" },
      "purpose": "Which portal users may see and edit this agent. Admins and managers only.",
      "tasks": ["give a colleague access to an agent"]
    },
    "agent.linked-tutors": {
      "route": "/tutors/:tutorId/linked-tutors",
      "area": "agents",
      "params": { "tutorId": "bot" },
      "purpose": "Agents linked to this Alpha Twin (dozent template only).",
      "tasks": ["link agents to a twin"]
    },
    "chat-styling": {
      "route": "/chat-styling",
      "area": "agents",
      "purpose": "Interface of the chat widget (menu item \"Interface\") — interface type (Liquid = the new layout with Orbi, apps beside the chat and a canvas; Chat = the previous chat window), colours, logo, chat bubble, font, pop-up message — as reusable stylings assigned to agents, with a live preview.",
      "tasks": ["change the chat colours", "upload a logo", "switch the interface type to Liquid or Chat", "assign a styling to an agent"]
    },
    "org.usage": {
      "route": "/usage",
      "area": "org",
      "purpose": "Consumption of the organisation (messages, tokens, credits) per period and agent.",
      "tasks": ["see how many credits are left", "see which agent uses most"]
    },
    "integrations.overview": {
      "route": "/integrations",
      "area": "lms",
      "purpose": "All integrations: learning platforms (Moodle plugin, LTI, ILIAS), cloud drives and website embedding.",
      "tasks": ["connect Moodle", "connect ILIAS", "connect a cloud drive"]
    },
    "integrations.moodle": {
      "route": "/moodle",
      "area": "lms",
      "purpose": "Moodle overview: courses, participants and learning progress synced from connected Moodle sites.",
      "tasks": ["see synced Moodle courses", "refresh Moodle data"]
    },
    "integrations.ilias": {
      "route": "/ilias",
      "area": "lms",
      "purpose": "ILIAS overview: what exists in the connected ILIAS installation (courses, members, learning progress). Agents are assigned on the placements page.",
      "tasks": ["see ILIAS courses"]
    },
    "org.products": {
      "route": "/products",
      "area": "org",
      "purpose": "Plans and credit packages. Buying and plan changes are done by the user themselves (payment).",
      "tasks": ["compare plans", "buy credits"]
    },
    "org.organisation": {
      "route": "/organisation",
      "area": "org",
      "purpose": "Organisation profile and billing address, users and roles, feature sets, API keys, credits and limits.",
      "tasks": ["invite a user", "change a user's role", "set credit limits"]
    },
    "agents.setup": {
      "route": "/agent-setup/:tab",
      "area": "agents",
      "tab_param": "tab",
      "tabs": ["learnings", "skills", "mcp", "guardrails"],
      "purpose": "Organisation-wide behaviour and capabilities for all agents: agent learnings, skills library, MCP connectors, rules and limits.",
      "tasks": ["add an organisation-wide rule", "add a skill", "add an MCP connector"]
    },
    "agents.mcp-connector": {
      "route": "/mcp-connectors/:connectorId",
      "area": "agents",
      "params": { "connectorId": "mcp_connector" },
      "purpose": "One MCP connector: server address, authentication, offered tools and the agents that use it.",
      "tasks": ["test an MCP connector", "choose which tools an agent may use"]
    },
    "agents.skill-editor": {
      "route": "/skills/:skillId",
      "area": "agents",
      "params": { "skillId": "skill" },
      "purpose": "Editor for one skill — a procedure an agent follows step by step. The id 'new' creates one.",
      "tasks": ["write a new skill", "edit a skill"]
    },
    "content.creator": {
      "route": "/creator",
      "area": "creator",
      "purpose": "Creator start: tiles to create a course (e-learning or Moodle) or an exercise (on a topic or from a knowledge base); a button opens the library. A material zone turns Word, PowerPoint, PDF, Markdown, text or an Articulate Rise package (SCORM .zip) into an e-learning or microlearning course, free of credits; very large documents ask first.",
      "tasks": ["create a course", "create an exercise", "open the creator library", "import a Word, PowerPoint, PDF or Markdown file as a course", "import an Articulate Rise course"]
    },
    "content.creator-new-course": {
      "route": "/creator/new-course",
      "area": "creator",
      "purpose": "The Creator start with the new-course dialog open: choose interactive e-learning (designed by hand in the course editor; with AI coming soon) or a Moodle course developed with AI in the chat.",
      "tasks": ["generate a Moodle course with AI", "build an e-learning course by hand"]
    },
    "content.creator-library": {
      "route": "/creator/library",
      "area": "creator",
      "purpose": "Library of the organisation's Creator content (exercises, Moodle courses, e-learning, microlearning) with type, status, where it is live and the last change; open, put into a knowledge base, share, delete, restore from the trash.",
      "tasks": ["find a course or exercise", "put an exercise into a knowledge base", "restore deleted content"]
    },
    "content.creator-workspace": {
      "route": "/creator/workspace",
      "area": "creator",
      "purpose": "The Creator workspace: AI chat and editors for one exercise or course (opened from the start page or the library).",
      "tasks": ["generate a course", "edit an exercise"]
    },
    "content.curriculum": {
      "route": "/curriculum",
      "area": "curriculum",
      "purpose": "Study programmes and curriculum items with learning goals; enrol learners.",
      "tasks": ["create a study programme", "import a curriculum"]
    },
    "content.course": {
      "route": "/courses/:courseId",
      "area": "courses",
      "params": { "courseId": "course" },
      "purpose": "One course: its structure, sections and lessons.",
      "tasks": ["edit the course structure"]
    },
    "content.lesson": {
      "route": "/courses/:courseId/lessons/:lessonId",
      "area": "courses",
      "params": { "courseId": "course", "lessonId": "lesson" },
      "purpose": "One lesson of a course with its content.",
      "tasks": ["edit a lesson"]
    },
    "analytics.courses": {
      "route": "/analytics/courses",
      "area": "analytics",
      "purpose": "Usage and learning analytics per course.",
      "tasks": ["compare courses by activity"]
    },
    "analytics.learners": {
      "route": "/analytics/learners",
      "area": "learners",
      "purpose": "Activity and progress per learner. Personal data.",
      "tasks": ["find inactive learners"]
    },
    "analytics.events": {
      "route": "/analytics/events",
      "area": "analytics",
      "purpose": "Raw analytics events with filters (messages, feedback, tool use).",
      "tasks": ["filter events by agent and period"]
    },
    "analytics.semantics": {
      "route": "/analytics/semantics",
      "area": "analytics",
      "purpose": "Topics and recurring questions learners ask, clustered by meaning.",
      "tasks": ["see the most frequent questions"]
    },
    "analytics.conversations": {
      "route": "/analytics/conversations",
      "area": "learners",
      "purpose": "Conversation browser: read real chats, filter by topic or rating, and turn findings into agent learnings.",
      "tasks": ["read conversations of an agent", "create a learning from a conversation"]
    },
    "analytics.live": {
      "route": "/analytics/live",
      "area": "analytics",
      "purpose": "Live view of currently active chat sessions.",
      "tasks": ["see who is chatting right now"]
    },
    "analytics.sessions": {
      "route": "/analytics/sessions",
      "area": "analytics",
      "purpose": "Chat sessions over time, per agent.",
      "tasks": ["see session trends"]
    },
    "analytics.credits": {
      "route": "/analytics/credits",
      "area": "org",
      "purpose": "Credit consumption of the organisation by activity and agent.",
      "tasks": ["see what credits were spent on"]
    },
    "analytics.dozent-students": {
      "route": "/analytics/dozent-students",
      "area": "dozent",
      "purpose": "Placeholder for the dozent's student analytics (currently shows the agent overview)."
    },
    "dozent.exercises": {
      "route": "/dozent/exercises",
      "area": "dozent",
      "purpose": "Exercises the dozent created and sent to learners, with their submissions.",
      "tasks": ["create an exercise", "review submissions"]
    },
    "dozent.exercise-editor": {
      "route": "/dozent/exercises/:id",
      "area": "dozent",
      "params": { "id": "exercise" },
      "purpose": "Editor for one exercise: questions, solution, recipients.",
      "tasks": ["edit an exercise", "send an exercise to learners"]
    },
    "dozent.participants": {
      "route": "/dozent/participants",
      "area": "dozent",
      "purpose": "Learners in the dozent's courses with their progress.",
      "tasks": ["see a learner's progress"]
    },
    "dozent.faqs": {
      "route": "/dozent/faqs",
      "area": "dozent",
      "purpose": "The dozent's FAQs that the agent uses to answer learners.",
      "tasks": ["add an FAQ"]
    },
    "dozent.messages": {
      "route": "/dozent/messages",
      "area": "dozent",
      "purpose": "Messages between learners and the dozent; reply to conversations.",
      "tasks": ["reply to a learner"]
    },
    "dozent.dozenten": {
      "route": "/dozent/dozenten",
      "area": "dozent",
      "purpose": "Dozents of the organisation and the agents they are linked to.",
      "tasks": ["see which dozent is linked to which agent"]
    },
    "admin.users": {
      "route": "/admin/users",
      "area": "system",
      "purpose": "Platform administration: all users across organisations.",
      "tasks": ["find a user"]
    },
    "admin.learners": {
      "route": "/admin/learners",
      "area": "system",
      "purpose": "Platform administration: all learners across organisations.",
      "tasks": ["find a learner"]
    },
    "admin.organisations": {
      "route": "/admin/organisations",
      "area": "system",
      "purpose": "Platform administration: all organisations with plans, quotas and trials.",
      "tasks": ["extend a trial", "change an organisation's quota"]
    },
    "admin.connections": {
      "route": "/admin/connections",
      "area": "system",
      "purpose": "Platform administration: all learning-platform connections across organisations.",
      "tasks": ["check a customer's Moodle connection"]
    },
    "admin.audit": {
      "route": "/admin/audit",
      "area": "system",
      "purpose": "Audit log of administrative changes.",
      "tasks": ["see who changed what"]
    },
    "admin.token-analytics": {
      "route": "/admin/token-analytics",
      "area": "system",
      "purpose": "Platform-wide token economy and margins (super admin).",
      "tasks": ["compare token costs per organisation"]
    },
    "admin.referrals": {
      "route": "/admin/referrals",
      "area": "system",
      "purpose": "Partner revenue share: partners, agreements, commissions and statements (super admin).",
      "tasks": ["release commissions"]
    },
    "admin.learning-block-cases": {
      "route": "/admin/learning-block-cases",
      "area": "system",
      "purpose": "Learning cases for the interactive learning aids in the chat: which component helps or should be avoided in which situation. Review the suggestions the nightly condensation of usage writes, approve, edit, retire or write cases by hand; only active cases reach the agents. Also shows how each component was used recently (super admin).",
      "tasks": ["approve a suggested learning case", "write a learning case by hand", "see how the learning aids were used"]
    },
    "admin.pricing": {
      "route": "/admin/pricing",
      "area": "system",
      "purpose": "Internal offer calculator: compare pricing scenarios side by side (learners, platform fee per learner, included credits, credit price, expected usage, BYOK factor, one-off fees). Every scenario is computed as managed (credits) and BYOK, per month and per year, with margins, a sensitivity matrix and a usage chart. Frontend only, nothing is saved server-side (super admin).",
      "tasks": ["compare offer scenarios", "find a platform fee for a target margin", "copy an offer summary"]
    }
  },
  "anchors": {
    "layout.main-nav": {
      "description": "Main navigation on the left with all areas of the portal.",
      "selector": "nb-sidebar[tag='menu-sidebar']"
    },
    "layout.header": {
      "description": "Top bar with the logo, notifications and the user menu.",
      "selector": "nb-layout-header"
    },
    "layout.notifications": {
      "description": "Bell with the user's notifications.",
      "selector": "app-dashboard-header .notification-area"
    },
    "layout.notification-suggestion": {
      "description": "A „Neuer Wissensvorschlag“ entry in the open notification list.",
      "selector": "app-dashboard-header .notification-item[data-notification-type='learning_suggestion']"
    },
    "layout.agent": {
      "description": "The Orbi button in the top bar that opens and closes this assistant (shortcut Cmd/Ctrl+K).",
      "selector": "app-dashboard-header .agent-button"
    },
    "agent.panel-input": {
      "description": "Input field of the Orbi panel where the person types a question to the portal assistant.",
      "selector": "app-agent-panel form.agent-panel__composer"
    },
    "layout.user-menu": {
      "description": "User menu: account settings, organisation, language, sign out.",
      "selector": "app-dashboard-header nb-user"
    },
    "page.header": {
      "description": "Title and short description of the current page.",
      "selector": "nb-layout-column .header"
    },
    "page.help": {
      "description": "Question-mark button that opens the longer explanation of the current page.",
      "selector": "nb-layout-column app-page-help"
    },
    "page.tabs": {
      "description": "Tabs of the current page.",
      "selector": "nb-layout-column nb-tabset"
    },
    "agent.menu": {
      "description": "Sub-navigation of one agent (general, AI configuration, knowledge, learning, behaviour, access).",
      "selector": "app-tutor-menu"
    },
    "agents.search": {
      "description": "Search field that filters the agent tiles by name.",
      "page": "agents.list"
    },
    "agents.template-filter": {
      "description": "Filter for the agent tiles by template (kind of agent).",
      "page": "agents.list"
    },
    "agents.embed": {
      "description": "Button that shows the code to embed an agent's chat on a website.",
      "page": "agents.list"
    },
    "agents.create": {
      "description": "Button that opens the wizard to create a new agent.",
      "page": "agents.list"
    },
    "creator.new-course": {
      "description": "Tile that opens the new-course dialog (interactive e-learning or Moodle course).",
      "page": "content.creator"
    },
    "creator.new-exercise": {
      "description": "Tile that opens the new-exercise dialog (on a topic or from a knowledge base).",
      "page": "content.creator"
    },
    "creator.library": {
      "description": "Button that opens the library of existing courses and exercises.",
      "page": "content.creator"
    },
    "creator.import": {
      "description": "Material zone: drop or choose a file to import it as a course — Word (.docx), PowerPoint (.pptx), PDF, Markdown, text, or an Articulate Rise package (SCORM, .zip). Documents ask first whether they should become e-learning or microlearning.",
      "page": "content.creator"
    },
    "creator.moodle-ai": {
      "description": "Button in the new-course dialog that develops a Moodle course with AI in the chat.",
      "page": "content.creator-new-course"
    },
    "creator.exercise-topic": {
      "description": "Option in the new-exercise dialog that develops questions on a topic in the chat.",
      "page": "content.creator"
    },
    "creator.exercise-knowledge": {
      "description": "Option in the new-exercise dialog that creates questions from chosen knowledge bases.",
      "page": "content.creator"
    },
    "creator.by-hand": {
      "description": "Option in the new-course dialog that creates an e-learning or microlearning course from the empty template.",
      "page": "content.creator-new-course"
    },
    "creator.format-elearning": {
      "description": "Card „Interaktives E-Learning“ in the new-course dialog (SCORM export for any LMS); picking it shows the ways to start.",
      "page": "content.creator-new-course"
    },
    "creator.moodle-start": {
      "description": "Button in the Moodle course dialog that opens the Creator with name and description and starts the course structure.",
      "page": "content.creator-new-course"
    },
    "creator.library-filter": {
      "description": "Filter of the library by type: all, exercises, courses.",
      "page": "content.creator-library"
    },
    "creator.library-course-filter": {
      "description": "Second filter shown under courses: all courses, Moodle, e-learning or microlearning.",
      "page": "content.creator-library"
    },
    "creator.library-search": {
      "description": "Search field that finds library content by part of its title, across all pages.",
      "page": "content.creator-library"
    },
    "creator.library-open": {
      "description": "„Öffnen“ in a library row — opens the course or exercise in the editor (the first row when several match).",
      "page": "content.creator-library"
    },
    "creator.trash": {
      "description": "Button that shows deleted content of the last 30 days to restore.",
      "page": "content.creator-library"
    },
    "knowledge.bucket-list": {
      "description": "Left panel: the list of knowledge bases (folders).",
      "page": "knowledge.base"
    },
    "academy.continue": {
      "description": "Top of the Academy: welcome with the overall progress and the recommended next lesson.",
      "page": "academy.home"
    },
    "academy.topics": {
      "description": "Tiles of the eight Academy topics with their progress.",
      "page": "academy.home"
    },
    "academy.certificates": {
      "description": "Card with the three certificates (Essentials, Professional, Expert) and what is still missing.",
      "page": "academy.home"
    },
    "academy.lesson-list": {
      "description": "The lessons of a topic in order, with their status.",
      "page": "academy.topic"
    },
    "academy.lesson-video": {
      "description": "Explainer video of the lesson.",
      "page": "academy.lesson"
    },
    "academy.lesson-quiz": {
      "description": "Mini quiz of the lesson with instant feedback.",
      "page": "academy.lesson"
    },
    "academy.try-in-portal": {
      "description": "„Selbst machen“ of a lesson: starts the guided click path through the portal and shows whether the step is already done; lessons without a click path show a button that opens the feature.",
      "page": "academy.lesson"
    },
    "academy.exam": {
      "description": "Final test of a certificate: start, questions and result.",
      "page": "academy.certificate"
    },
    "agents.card-play": {
      "description": "Play button on an agent tile: opens the preview chat to try the agent.",
      "page": "agents.list"
    },
    "agent.onboarding-tour": {
      "description": "Switch for the chat widget's onboarding tour on the agent's general page (topics and how often it is offered appear below it).",
      "page": "agent.general"
    },
    "chat-styling.interface": {
      "description": "Switch between the two widget interfaces: Liquid (Orbi with input bar and apps) or Chat (window).",
      "page": "chat-styling"
    },
    "chat-styling.settings": {
      "description": "Left card of the Interface page: colours, font, logo, widget trigger and the other styling fields.",
      "page": "chat-styling"
    },
    "chat-styling.preview": {
      "description": "Live preview of the chat widget on the Interface page.",
      "page": "chat-styling"
    },
    "agent.linked-tutors-list": {
      "description": "Linked tutors of an Alpha Twin: the tutors whose knowledge the twin uses.",
      "page": "agent.linked-tutors"
    },
    "dozent.learner-list": {
      "description": "Left list of the lecturer's messages: learners, yellow markers for unread conversations.",
      "page": "dozent.messages"
    },
    "dozent.thread": {
      "description": "Right panel of the lecturer's messages: the conversation with the selected learner and the reply field.",
      "page": "dozent.messages"
    },
    "dozent.exercise-create": {
      "description": "Button that creates a new exercise (on a topic, from the knowledge base, or by hand).",
      "page": "dozent.exercises"
    },
    "dozent.faq-card": {
      "description": "The lecturer's FAQ and knowledge entries with their tabs, search and create button.",
      "page": "dozent.faqs"
    },
    "dozent.course-list": {
      "description": "Left list of courses on „Kurse & Teilnehmende“; a click shows its participants.",
      "page": "dozent.participants"
    },
    "dozent.participant-detail": {
      "description": "Right panel on „Kurse & Teilnehmende“: participants of the selected course, their learner and competency profile.",
      "page": "dozent.participants"
    },
    "analytics.conversations": {
      "description": "The conversations of the agents with filters for period, agent and course.",
      "page": "analytics.conversations"
    },
    "integrations.groups": {
      "description": "Tiles of all integrations, grouped (learning platforms, cloud drives, …).",
      "page": "integrations.overview"
    },
    "org.plans": {
      "description": "The product plans with their prices and the button to start the trial period.",
      "page": "org.products"
    },
    "agents.setup-card": {
      "description": "Card with the tabs of „Fähigkeiten“: learnings, guardrails, skills, MCP connectors.",
      "page": "agents.setup"
    },
    "agents.grid": {
      "description": "The agent tiles of the organisation; a click on a tile opens that agent.",
      "page": "agents.list"
    },
    "agent.knowledge-link": {
      "description": "Section „Wissensbasis – Verknüpfung“ on the agent's knowledge tab: tick the knowledge bases the agent answers from.",
      "page": "agent.knowledge"
    },
    "placements.table": {
      "description": "The placements of all connected LMS: which course has which agent, course sync, test and actions.",
      "page": "agents.placements"
    },
    "placements.course-sync": {
      "description": "Column „Kurs-Sync“ of the placements: makes the course's content the agent's knowledge.",
      "page": "agents.placements"
    },
    "knowledge.create-bucket": {
      "description": "Plus button that creates a new knowledge base.",
      "page": "knowledge.base"
    },
    "knowledge.bucket-content": {
      "description": "Right panel: files, websites and cloud sources of the selected knowledge base.",
      "page": "knowledge.base"
    },
    "agents.dialog-name": {
      "description": "Name field on the last stage of the create-agent dialog.",
      "page": "agents.list"
    },
    "agents.dialog-next": {
      "description": "Primary button of the create-agent dialog: „Weiter“, on the last stage „Erstellen“.",
      "page": "agents.list"
    },
    "agent.name": {
      "description": "Name row on the agent's Allgemein page.",
      "page": "agent.general"
    },
    "agent.welcome-message": {
      "description": "Welcome message row on the agent's Allgemein page.",
      "page": "agent.general"
    },
    "agent.widget-style": {
      "description": "„Chat-Styling“ row on the agent's Allgemein page: which Interface styling this agent uses.",
      "page": "agent.general"
    },
    "agent.onboarding-topics": {
      "description": "„Erklärte Funktionen“ of the onboarding tour (only while the tour is on).",
      "page": "agent.general"
    },
    "agent.onboarding-max": {
      "description": "„Automatisch höchstens anzeigen“ of the onboarding tour (only while the tour is on).",
      "page": "agent.general"
    },
    "agent.knowledge-folders": {
      "description": "The folder tiles on Agent › Wissen › Wissensbasis — a click links or unlinks a folder.",
      "page": "agent.knowledge"
    },
    "agent.twin-attach": {
      "description": "„Tutor verknüpfen“ on a Twin's Verlinkte Tutoren page.",
      "page": "agent.linked-tutors"
    },
    "agent.twin-attach-select": {
      "description": "Tutor picker after „Tutor verknüpfen“.",
      "page": "agent.linked-tutors"
    },
    "agent.twin-attach-confirm": {
      "description": "„Verknüpfen“ button that links the picked tutor.",
      "page": "agent.linked-tutors"
    },
    "knowledge.bucket-name": {
      "description": "Name field in the create/edit-folder dialog.",
      "page": "knowledge.base"
    },
    "knowledge.bucket-save": {
      "description": "„Speichern“ in the create/edit-folder dialog.",
      "page": "knowledge.base"
    },
    "knowledge.upload-drop": {
      "description": "Drop zone in the file upload dialog.",
      "page": "knowledge.base"
    },
    "knowledge.upload-submit": {
      "description": "„Hochladen“ in the file upload dialog.",
      "page": "knowledge.base"
    },
    "chat-styling.select": {
      "description": "Picker of the styling being edited, top right on Interface.",
      "page": "chat-styling"
    },
    "chat-styling.add": {
      "description": "„Hinzufügen“ — creates a new styling.",
      "page": "chat-styling"
    },
    "dozent.learner-row": {
      "description": "A learner row in Dozent › Nachrichten.",
      "page": "dozent.messages"
    },
    "dozent.conversation": {
      "description": "A conversation in the selected learner's thread.",
      "page": "dozent.messages"
    },
    "dozent.resolve": {
      "description": "„Als geklärt markieren“ of an open conversation.",
      "page": "dozent.messages"
    },
    "dozent.composer": {
      "description": "Reply field of an open conversation.",
      "page": "dozent.messages"
    },
    "dozent.exercise-scratch": {
      "description": "„Von Grund auf erstellen“ in the Übung-erstellen menu.",
      "page": "dozent.exercises"
    },
    "dozent.exercise-from-kb": {
      "description": "„Aus Wissensbasis generieren“ in the Übung-erstellen menu.",
      "page": "dozent.exercises"
    },
    "exercise.title": {
      "description": "Title field in the create-exercise dialog.",
      "page": "dozent.exercises"
    },
    "exercise.description": {
      "description": "Description field in the create-exercise dialog.",
      "page": "dozent.exercises"
    },
    "exercise.types": {
      "description": "Exercise types picker in the create-exercise dialog.",
      "page": "dozent.exercises"
    },
    "exercise.kb-list": {
      "description": "Knowledge folders to generate from, in the create-exercise dialog.",
      "page": "dozent.exercises"
    },
    "dozent.faq-add": {
      "description": "„Wissen hinzufügen“ menu button in Dozent › FAQ / Wissen.",
      "page": "dozent.faqs"
    },
    "dozent.faq-add-single": {
      "description": "„Wissen hinzufügen“ entry of that menu (one entry).",
      "page": "dozent.faqs"
    },
    "dozent.faq-add-empty": {
      "description": "„Wissen hinzufügen“ in the empty state of FAQ / Wissen.",
      "page": "dozent.faqs"
    },
    "faq.question": { "description": "Question field in the FAQ dialog.", "page": "dozent.faqs" },
    "faq.answer": { "description": "Answer field in the FAQ dialog.", "page": "dozent.faqs" },
    "faq.dozent": {
      "description": "„Dozent(en) zuordnen“ in the FAQ dialog.",
      "page": "dozent.faqs"
    },
    "dozent.course-row": {
      "description": "A course in the course list of Kurse & Teilnehmende.",
      "page": "dozent.participants"
    },
    "dozent.participant-name": {
      "description": "A learner's name in the Teilnehmende tab — opens the learner's profile.",
      "page": "dozent.participants"
    },
    "placements.row-agent": {
      "description": "Agent picker in a course row of the Moodle placements.",
      "page": "agents.placements"
    },
    "placements.row-sync": {
      "description": "Kurs-Sync switch in a course row of the Moodle placements.",
      "page": "agents.placements"
    },
    "placements.row-save": {
      "description": "„Speichern“ of a changed course row.",
      "page": "agents.placements"
    },
    "org.add-user": {
      "description": "„Hinzufügen“ on Organisation › Nutzer & Rechte — invites a user.",
      "page": "org.organisation"
    },
    "academy.filter": {
      "description": "Filter of the Academy start page (Alle, Offen, Abgeschlossen, Portalhinweise).",
      "page": "academy.home"
    },
    "academy.search": {
      "description": "Search field of the Academy start page.",
      "page": "academy.home"
    },
    "analytics.agent-filter": {
      "description": "Agent filter on Analytics › Echtzeit.",
      "page": "analytics.live"
    },
    "analytics.live-kpis": {
      "description": "Key figures at the top of Analytics › Echtzeit.",
      "page": "analytics.live"
    },
    "analytics.conversation-row": {
      "description": "One conversation row on Analytics › Konversationen (click opens it).",
      "page": "analytics.conversations"
    },
    "analytics.conversation-filters": {
      "description": "Time-range buttons on Analytics › Konversationen.",
      "page": "analytics.conversations"
    },
    "analytics.rating-filter": {
      "description": "Rating filter on Analytics › Konversationen (all / thumbs down / thumbs down with reason / thumbs up).",
      "page": "analytics.conversations"
    },
    "analytics.feedback-reason": {
      "description": "The learner's reason for a thumbs-down under an answer of an opened conversation.",
      "page": "analytics.conversations"
    },
    "moodle.url": {
      "description": "Moodle URL field in the Moodle connect dialog (only while not connected).",
      "page": "integrations.overview"
    },
    "moodle.api-key": {
      "description": "API-key picker in the Moodle connect dialog.",
      "page": "integrations.overview"
    },
    "moodle.copy-key": {
      "description": "Copy button next to the API-key picker in the Moodle connect dialog.",
      "page": "integrations.overview"
    },
    "dialog.primary": {
      "description": "The primary (blue) button at the bottom right of the open dialog.",
      "selector": "nb-dialog-container nb-card-footer button[status='primary']"
    },
    "dialog.card": {
      "description": "The open dialog as a whole.",
      "selector": "nb-dialog-container nb-card"
    },
    "layout.nav-agent-setup": {
      "description": "„Fähigkeiten“ under KI-Agenten in the main navigation.",
      "selector": "nb-sidebar[tag='menu-sidebar'] a[href='/agent-setup']"
    },
    "analytics.learn-from": {
      "description": "„Daraus lernen“ under an answer of an opened conversation — turns it into a learning draft.",
      "page": "analytics.conversations"
    },
    "placements.connect-site": {
      "description": "„Moodle verbinden“ on the Moodle tab of Platzierungen while no Moodle site is connected.",
      "page": "agents.placements"
    },
    "analytics.conversation-thread": {
      "description": "Questions and answers of the opened conversation on Analytics › Konversationen.",
      "page": "analytics.conversations"
    }
  },
  "anchor_families": {
    "agentmenu": {
      "pattern": "agentmenu.{path}",
      "description": "An entry of an agent's own menu by the end of its link: general, ai-settings, knowledge, learning, setup, access, linked-tutors.",
      "selector": "app-tutor-menu nb-menu a[href$='/{value}']"
    },
    "aisection": {
      "pattern": "aisection.{section}",
      "description": "A sub-entry of „KI-Konfiguration“ in an agent's menu by its section key (e.g. aisection.basis_persona, aisection.guardrails).",
      "selector": "app-tutor-menu nb-menu a[href$='/ai-settings/{value}']"
    },
    "navgroup": {
      "pattern": "navgroup.{page}",
      "description": "The collapsible group of the main navigation that contains this page's entry (e.g. navgroup.dozent.messages = „Dozent“).",
      "selector": "nb-sidebar[tag='menu-sidebar'] li.menu-item:has(> ul a[href='{route}']) > a"
    },
    "agentcard": {
      "pattern": "agentcard.{template}",
      "description": "The first agent tile of a template on the agents page (e.g. agentcard.alpha_tutor, agentcard.alpha_dozent)."
    },
    "tab": {
      "pattern": "tab.{key}",
      "description": "A tab of an agent page or of „Fähigkeiten“ by its key (e.g. tab.base, tab.usage, tab.didaktik, tab.learnings)."
    },
    "setting": {
      "pattern": "setting.{variable_name}",
      "description": "One AI-setting row of an agent, by the setting's variable_name (e.g. setting.default_language). Shown on the agent's AI-configuration, knowledge and learning pages."
    },
    "nav": {
      "pattern": "nav.{page}",
      "description": "The main-navigation entry that leads to a page without route parameters (e.g. nav.knowledge.base).",
      "selector": "nb-sidebar[tag='menu-sidebar'] a[href='{route}']"
    },
    "agenttemplate": {
      "pattern": "agenttemplate.{template}",
      "description": "A template card on the first stage of the create-agent dialog (e.g. agenttemplate.alpha_tutor, agenttemplate.alpha_dozent)."
    },
    "agentvariant": {
      "pattern": "agentvariant.{template}",
      "description": "A variant card on the second stage of the create-agent dialog, after „Alpha Tutor“ (agentvariant.alpha_tutor = Starter, agentvariant.alpha_flexible = Pro)."
    },
    "agentplay": {
      "pattern": "agentplay.{template}",
      "description": "The „Try me“ button on the first agent tile of a template (opens the preview chat).",
      "selector": "[data-al-anchor='agentcard.{value}'] [data-al-anchor='agents.card-play']"
    },
    "pagetab": {
      "pattern": "pagetab.{tabId}",
      "description": "A tab of an nb-tabset by its tabId (e.g. pagetab.users on Organisation, pagetab.bubble on Interface, pagetab.lms on Platzierungen, pagetab.profile in a learner's profile).",
      "selector": "nb-tabset li.tab[data-tab-id='{value}']"
    },
    "usermenu": {
      "pattern": "usermenu.{page}",
      "description": "An entry of the user menu behind the name in the header, by the page it opens (usermenu.org.products, usermenu.org.organisation). The menu must be open.",
      "selector": "nb-context-menu a[href='{route}']"
    },
    "tableaction": {
      "pattern": "tableaction.{key}",
      "description": "A button above a data table (app-table global action) by its key (e.g. tableaction.add = „Dateien hochladen“ in the Wissensbasis, tableaction.create = „Learning hinzufügen“)."
    },
    "integration": {
      "pattern": "integration.{link}",
      "description": "A tile on the Integrations page by its link (integration.moodle, integration.lti, integration.ilias, integration.google_drive)."
    }
  },
  "forms": {},
  "dialogs": {},
  "tours": {
    "academy.create-knowledge-base": {
      "purpose": "Academy click path: create a folder and upload files.",
      "steps": [
        { "anchor": "nav.knowledge.base", "advance": "click" },
        {
          "anchor": "knowledge.create-bucket",
          "page": "knowledge.base",
          "await": true,
          "advance": "click"
        },
        { "anchor": "knowledge.bucket-name", "page": "knowledge.base", "await": true },
        {
          "anchor": "knowledge.bucket-save",
          "page": "knowledge.base",
          "await": true,
          "advance": "click"
        },
        { "anchor": "knowledge.bucket-content", "page": "knowledge.base", "await": true },
        {
          "anchor": "tableaction.add",
          "page": "knowledge.base",
          "await": true,
          "advance": "click"
        },
        { "anchor": "knowledge.upload-drop", "page": "knowledge.base", "await": true },
        {
          "anchor": "knowledge.upload-submit",
          "page": "knowledge.base",
          "await": true,
          "advance": "click"
        },
        { "anchor": "knowledge.bucket-content", "page": "knowledge.base", "await": true }
      ]
    },
    "academy.link-knowledge-base": {
      "purpose": "Academy click path: link a folder with an agent.",
      "steps": [
        { "anchor": "nav.agents.list", "advance": "click" },
        {
          "anchor": "agentcard.alpha_tutor",
          "page": "agents.list",
          "await": true,
          "advance": "click",
          "alt": ["agentcard.alpha_flexible", "agentcard.alpha_support", "agentcard.alpha_assist", "agentcard.alpha_basic"]
        },
        {
          "anchor": "agentmenu.knowledge",
          "page": "agent.general",
          "await": true,
          "advance": "click"
        },
        { "anchor": "agent.knowledge-folders", "page": "agent.knowledge", "await": true },
        { "anchor": "tab.usage", "page": "agent.knowledge", "advance": "click" },
        { "anchor": "setting.knowledge_mode", "page": "agent.knowledge", "await": true },
        { "anchor": "setting.include_sources", "page": "agent.knowledge" }
      ]
    },
    "academy.knowledge-mode": {
      "purpose": "Academy click path: restrict an agent to its knowledge base.",
      "steps": [
        { "anchor": "nav.agents.list", "advance": "click" },
        {
          "anchor": "agentcard.alpha_tutor",
          "page": "agents.list",
          "await": true,
          "advance": "click",
          "alt": ["agentcard.alpha_flexible", "agentcard.alpha_support", "agentcard.alpha_assist", "agentcard.alpha_basic"]
        },
        {
          "anchor": "agentmenu.knowledge",
          "page": "agent.general",
          "await": true,
          "advance": "click"
        },
        { "anchor": "tab.usage", "page": "agent.knowledge", "await": true, "advance": "click" },
        { "anchor": "setting.knowledge_mode", "page": "agent.knowledge", "await": true },
        { "anchor": "setting.search_web", "page": "agent.knowledge" },
        { "anchor": "setting.sources_links_enabled", "page": "agent.knowledge" }
      ]
    },
    "academy.course-sync": {
      "purpose": "Academy click path: sync a Moodle course into its own folder.",
      "steps": [
        { "anchor": "nav.agents.placements", "advance": "click" },
        {
          "anchor": "pagetab.lms",
          "page": "agents.placements",
          "await": true,
          "advance": "click",
          "alt": ["pagetab.ilias"]
        },
        {
          "anchor": "placements.row-agent",
          "page": "agents.placements",
          "await": true,
          "alt": ["placements.connect-site", "placements.table"]
        },
        { "anchor": "placements.course-sync", "page": "agents.placements" },
        { "anchor": "placements.row-sync", "page": "agents.placements" },
        { "anchor": "placements.row-save", "page": "agents.placements" }
      ]
    },
    "academy.portal-tour": {
      "purpose": "Academy click path: find your way around the portal.",
      "steps": [
        { "anchor": "layout.main-nav" },
        { "anchor": "nav.knowledge.base", "advance": "click" },
        { "anchor": "page.header", "page": "knowledge.base", "await": true },
        { "anchor": "page.help", "page": "knowledge.base", "advance": "click" },
        { "anchor": "layout.agent" },
        { "anchor": "layout.notifications" },
        { "anchor": "layout.user-menu" },
        { "anchor": "nav.academy.home" }
      ]
    },
    "academy.portal-assistant": {
      "purpose": "Academy click path: ask Orbi, the portal assistant.",
      "steps": [
        { "anchor": "layout.agent", "advance": "click" },
        { "anchor": "agent.panel-input", "await": true },
        { "anchor": "layout.agent" }
      ]
    },
    "academy.hints-and-help": {
      "purpose": "Academy click path: the Academy start page, search, filter and portal hints.",
      "steps": [
        { "anchor": "nav.academy.home", "advance": "click" },
        { "anchor": "academy.continue", "page": "academy.home", "await": true },
        { "anchor": "academy.search", "page": "academy.home" },
        { "anchor": "academy.filter", "page": "academy.home" },
        { "anchor": "academy.topics", "page": "academy.home" },
        { "anchor": "academy.certificates", "page": "academy.home" }
      ]
    },
    "academy.create-agent": {
      "purpose": "Academy click path: create an agent from a template.",
      "steps": [
        { "anchor": "nav.agents.list", "advance": "click" },
        { "anchor": "agents.create", "page": "agents.list", "await": true, "advance": "click" },
        {
          "anchor": "agenttemplate.alpha_tutor",
          "page": "agents.list",
          "await": true,
          "advance": "click"
        },
        {
          "anchor": "agentvariant.alpha_tutor",
          "page": "agents.list",
          "await": true,
          "advance": "click"
        },
        {
          "anchor": "agents.dialog-next",
          "page": "agents.list",
          "await": true,
          "advance": "click"
        },
        { "anchor": "agents.dialog-name", "page": "agents.list", "await": true },
        {
          "anchor": "agents.dialog-next",
          "page": "agents.list",
          "await": true,
          "advance": "click"
        },
        {
          "anchor": "agentcard.alpha_tutor",
          "page": "agents.list",
          "await": true,
          "alt": ["agents.grid"]
        }
      ]
    },
    "academy.agent-templates": {
      "purpose": "Academy click path: the five agent templates.",
      "steps": [
        { "anchor": "nav.agents.list", "advance": "click" },
        { "anchor": "agentcard.alpha_tutor", "page": "agents.list", "await": true },
        { "anchor": "agentcard.alpha_flexible", "page": "agents.list" },
        { "anchor": "agentcard.alpha_dozent", "page": "agents.list" },
        { "anchor": "agentcard.alpha_support", "page": "agents.list" },
        { "anchor": "agentcard.alpha_assist", "page": "agents.list" },
        { "anchor": "agents.template-filter", "page": "agents.list" },
        { "anchor": "agents.create", "page": "agents.list" }
      ]
    },
    "academy.ai-configuration": {
      "purpose": "Academy click path: language, tone, form of address and model of an agent.",
      "steps": [
        { "anchor": "nav.agents.list", "advance": "click" },
        {
          "anchor": "agentcard.alpha_tutor",
          "page": "agents.list",
          "await": true,
          "advance": "click",
          "alt": ["agentcard.alpha_flexible", "agentcard.alpha_support", "agentcard.alpha_assist", "agentcard.alpha_basic"]
        },
        {
          "anchor": "aisection.basis_persona",
          "page": "agent.general",
          "await": true,
          "advance": "click"
        },
        { "anchor": "setting.default_language", "page": "agent.ai-settings", "await": true },
        { "anchor": "setting.tone", "page": "agent.ai-settings" },
        { "anchor": "setting.formal_address", "page": "agent.ai-settings" },
        { "anchor": "setting.system_prompt_instructions", "page": "agent.ai-settings" },
        { "anchor": "aisection.model_limits", "page": "agent.ai-settings", "advance": "click" },
        { "anchor": "setting.ai_provider", "page": "agent.ai-settings", "await": true }
      ]
    },
    "academy.try-agent": {
      "purpose": "Academy click path: try an agent in the preview chat.",
      "steps": [
        { "anchor": "nav.agents.list", "advance": "click" },
        {
          "anchor": "agentplay.alpha_tutor",
          "page": "agents.list",
          "await": true,
          "advance": "click",
          "alt": ["agentplay.alpha_flexible", "agentplay.alpha_support", "agentplay.alpha_assist", "agents.card-play"]
        },
        {
          "anchor": "agentplay.alpha_tutor",
          "page": "agents.list",
          "alt": ["agentplay.alpha_flexible", "agentplay.alpha_support", "agentplay.alpha_assist", "agents.card-play"]
        }
      ]
    },
    "academy.widget-interface": {
      "purpose": "Academy click path: choose the chat interface and give it to an agent.",
      "steps": [
        { "anchor": "nav.chat-styling", "advance": "click" },
        {
          "anchor": "chat-styling.select",
          "page": "chat-styling",
          "await": true,
          "alt": ["chat-styling.add"]
        },
        { "anchor": "chat-styling.interface", "page": "chat-styling", "await": true },
        { "anchor": "chat-styling.preview", "page": "chat-styling" },
        { "anchor": "nav.agents.list", "advance": "click" },
        {
          "anchor": "agentcard.alpha_tutor",
          "page": "agents.list",
          "await": true,
          "advance": "click",
          "alt": ["agentcard.alpha_flexible", "agentcard.alpha_support", "agentcard.alpha_assist", "agentcard.alpha_basic"]
        },
        { "anchor": "agent.widget-style", "page": "agent.general", "await": true }
      ]
    },
    "academy.widget-styling": {
      "purpose": "Academy click path: colours, logo and chat bubble.",
      "steps": [
        { "anchor": "nav.chat-styling", "advance": "click" },
        {
          "anchor": "chat-styling.select",
          "page": "chat-styling",
          "await": true,
          "alt": ["chat-styling.add"]
        },
        { "anchor": "pagetab.window", "page": "chat-styling", "await": true, "advance": "click" },
        { "anchor": "chat-styling.settings", "page": "chat-styling" },
        { "anchor": "pagetab.bubble", "page": "chat-styling", "advance": "click" },
        { "anchor": "pagetab.aids", "page": "chat-styling", "advance": "click" },
        { "anchor": "chat-styling.preview", "page": "chat-styling" }
      ]
    },
    "academy.learning-aids": {
      "purpose": "Academy click path: switch on interactive learning aids.",
      "steps": [
        { "anchor": "nav.agents.list", "advance": "click" },
        {
          "anchor": "agentcard.alpha_tutor",
          "page": "agents.list",
          "await": true,
          "advance": "click",
          "alt": ["agentcard.alpha_flexible", "agentcard.alpha_support", "agentcard.alpha_assist", "agentcard.alpha_basic"]
        },
        {
          "anchor": "agentmenu.learning",
          "page": "agent.general",
          "await": true,
          "advance": "click"
        },
        { "anchor": "tab.didaktik", "page": "agent.learning", "await": true },
        { "anchor": "setting.learning_blocks_enabled", "page": "agent.learning", "await": true },
        { "anchor": "setting.learning_blocks_frequency", "page": "agent.learning", "await": true }
      ]
    },
    "academy.onboarding-tour": {
      "purpose": "Academy click path: the onboarding tour of the chat.",
      "steps": [
        { "anchor": "nav.agents.list", "advance": "click" },
        {
          "anchor": "agentcard.alpha_tutor",
          "page": "agents.list",
          "await": true,
          "advance": "click",
          "alt": ["agentcard.alpha_flexible", "agentcard.alpha_support", "agentcard.alpha_assist", "agentcard.alpha_basic"]
        },
        { "anchor": "agent.onboarding-tour", "page": "agent.general", "await": true },
        { "anchor": "agent.onboarding-topics", "page": "agent.general", "await": true },
        { "anchor": "agent.onboarding-max", "page": "agent.general", "await": true }
      ]
    },
    "academy.alpha-twin": {
      "purpose": "Academy click path: link tutors with an Alpha Twin.",
      "steps": [
        { "anchor": "nav.agents.list", "advance": "click" },
        {
          "anchor": "agentcard.alpha_dozent",
          "page": "agents.list",
          "await": true,
          "advance": "click",
          "alt": ["agents.create"]
        },
        {
          "anchor": "agentmenu.linked-tutors",
          "page": "agent.general",
          "await": true,
          "advance": "click"
        },
        {
          "anchor": "agent.twin-attach",
          "page": "agent.linked-tutors",
          "await": true,
          "advance": "click",
          "alt": ["agent.linked-tutors-list"]
        },
        { "anchor": "agent.twin-attach-select", "page": "agent.linked-tutors", "await": true },
        {
          "anchor": "agent.twin-attach-confirm",
          "page": "agent.linked-tutors",
          "await": true,
          "advance": "click"
        },
        { "anchor": "agent.linked-tutors-list", "page": "agent.linked-tutors", "await": true }
      ]
    },
    "academy.human-in-the-loop": {
      "purpose": "Academy click path: answer forwarded questions as a lecturer.",
      "steps": [
        {
          "anchor": "navgroup.dozent.messages",
          "advance": "click",
          "skip_if_visible": "nav.dozent.messages"
        },
        { "anchor": "nav.dozent.messages", "advance": "click" },
        {
          "anchor": "dozent.learner-row",
          "page": "dozent.messages",
          "await": true,
          "advance": "click",
          "alt": ["dozent.learner-list"]
        },
        {
          "anchor": "dozent.conversation",
          "page": "dozent.messages",
          "await": true,
          "advance": "click",
          "alt": ["dozent.thread"]
        },
        {
          "anchor": "dozent.composer",
          "page": "dozent.messages",
          "await": true,
          "alt": ["dozent.thread"]
        },
        {
          "anchor": "dozent.resolve",
          "page": "dozent.messages",
          "advance": "click",
          "alt": ["dozent.thread"]
        },
        { "anchor": "layout.notifications", "advance": "click" },
        {
          "anchor": "layout.notification-suggestion",
          "await": true,
          "advance": "click",
          "alt": ["layout.notifications"]
        },
        { "anchor": "faq.question", "page": "dozent.faqs", "await": true },
        { "anchor": "faq.answer", "page": "dozent.faqs" },
        { "anchor": "faq.dozent", "page": "dozent.faqs" },
        { "anchor": "dialog.primary", "page": "dozent.faqs" }
      ]
    },
    "academy.lecturer-exercises": {
      "purpose": "Academy click path: generate an exercise from the knowledge base.",
      "steps": [
        {
          "anchor": "navgroup.dozent.exercises",
          "advance": "click",
          "skip_if_visible": "nav.dozent.exercises"
        },
        { "anchor": "nav.dozent.exercises", "advance": "click" },
        {
          "anchor": "dozent.exercise-create",
          "page": "dozent.exercises",
          "await": true,
          "advance": "click"
        },
        {
          "anchor": "dozent.exercise-from-kb",
          "page": "dozent.exercises",
          "await": true,
          "advance": "click"
        },
        { "anchor": "exercise.title", "page": "dozent.exercises", "await": true },
        { "anchor": "exercise.description", "page": "dozent.exercises" },
        { "anchor": "dialog.primary", "page": "dozent.exercises", "advance": "click" },
        { "anchor": "exercise.kb-list", "page": "dozent.exercises", "await": true },
        { "anchor": "dialog.primary", "page": "dozent.exercises", "advance": "click" },
        { "anchor": "exercise.types", "page": "dozent.exercises", "await": true },
        { "anchor": "dialog.primary", "page": "dozent.exercises" }
      ]
    },
    "academy.lecturer-faqs": {
      "purpose": "Academy click path: add an FAQ entry for your Twin.",
      "steps": [
        {
          "anchor": "navgroup.dozent.faqs",
          "advance": "click",
          "skip_if_visible": "nav.dozent.faqs"
        },
        { "anchor": "nav.dozent.faqs", "advance": "click" },
        {
          "anchor": "dozent.faq-add",
          "page": "dozent.faqs",
          "await": true,
          "advance": "click",
          "alt": ["dozent.faq-add-empty"]
        },
        {
          "anchor": "dozent.faq-add-single",
          "page": "dozent.faqs",
          "await": true,
          "advance": "click",
          "skip_if_visible": "faq.question"
        },
        { "anchor": "faq.question", "page": "dozent.faqs", "await": true },
        { "anchor": "faq.answer", "page": "dozent.faqs" },
        { "anchor": "faq.dozent", "page": "dozent.faqs" },
        { "anchor": "dialog.primary", "page": "dozent.faqs" }
      ]
    },
    "academy.analytics-overview": {
      "purpose": "Academy click path: the Analytics pages.",
      "steps": [
        {
          "anchor": "navgroup.analytics.live",
          "advance": "click",
          "skip_if_visible": "nav.analytics.live"
        },
        { "anchor": "nav.analytics.live", "advance": "click" },
        { "anchor": "analytics.live-kpis", "page": "analytics.live", "await": true },
        { "anchor": "analytics.agent-filter", "page": "analytics.live" },
        { "anchor": "nav.analytics.events", "page": "analytics.live" },
        { "anchor": "nav.analytics.conversations", "page": "analytics.live" }
      ]
    },
    "academy.conversation-insights": {
      "purpose": "Academy click path: read conversations and learn from them.",
      "steps": [
        {
          "anchor": "navgroup.analytics.conversations",
          "advance": "click",
          "skip_if_visible": "nav.analytics.conversations"
        },
        { "anchor": "nav.analytics.conversations", "advance": "click" },
        {
          "anchor": "analytics.conversation-filters",
          "page": "analytics.conversations",
          "await": true
        },
        {
          "anchor": "analytics.conversation-row",
          "page": "analytics.conversations",
          "await": true,
          "advance": "click",
          "alt": ["analytics.conversations"]
        },
        {
          "anchor": "analytics.learn-from",
          "page": "analytics.conversations",
          "await": true,
          "alt": ["analytics.conversation-thread", "analytics.conversations"]
        }
      ]
    },
    "academy.learner-profiles": {
      "purpose": "Academy click path: open a learner's competency and learner profile.",
      "steps": [
        {
          "anchor": "navgroup.dozent.participants",
          "advance": "click",
          "skip_if_visible": "nav.dozent.participants"
        },
        { "anchor": "nav.dozent.participants", "advance": "click" },
        {
          "anchor": "dozent.course-row",
          "page": "dozent.participants",
          "await": true,
          "advance": "click",
          "alt": ["dozent.course-list"]
        },
        {
          "anchor": "pagetab.participants",
          "page": "dozent.participants",
          "await": true,
          "advance": "click"
        },
        {
          "anchor": "dozent.participant-name",
          "page": "dozent.participants",
          "await": true,
          "advance": "click",
          "alt": ["dozent.participant-detail"]
        },
        {
          "anchor": "pagetab.learning-state",
          "page": "dozent.participants",
          "await": true,
          "advance": "click"
        },
        {
          "anchor": "pagetab.profile",
          "page": "dozent.participants",
          "await": true,
          "advance": "click"
        }
      ]
    },
    "academy.connect-lms": {
      "purpose": "Academy click path: connect Moodle (or another LMS via LTI 1.3).",
      "steps": [
        { "anchor": "nav.integrations.overview", "advance": "click" },
        {
          "anchor": "integration.moodle",
          "page": "integrations.overview",
          "await": true,
          "advance": "click",
          "alt": ["integration.lti"]
        },
        {
          "anchor": "moodle.url",
          "page": "integrations.overview",
          "await": true,
          "alt": ["dialog.card"]
        },
        { "anchor": "moodle.api-key", "page": "integrations.overview" },
        { "anchor": "moodle.copy-key", "page": "integrations.overview" },
        { "anchor": "dialog.primary", "page": "integrations.overview" }
      ]
    },
    "academy.place-agents": {
      "purpose": "Academy click path: put an agent into a course.",
      "steps": [
        { "anchor": "nav.agents.placements", "advance": "click" },
        {
          "anchor": "pagetab.lms",
          "page": "agents.placements",
          "await": true,
          "advance": "click",
          "alt": ["pagetab.lti"]
        },
        {
          "anchor": "placements.row-agent",
          "page": "agents.placements",
          "await": true,
          "alt": ["placements.connect-site", "placements.table"]
        },
        { "anchor": "placements.row-save", "page": "agents.placements" },
        { "anchor": "pagetab.agents", "page": "agents.placements", "advance": "click" }
      ]
    },
    "academy.users-and-roles": {
      "purpose": "Academy click path: invite a user with a role.",
      "steps": [
        { "anchor": "layout.user-menu", "advance": "click" },
        { "anchor": "usermenu.org.organisation", "await": true, "advance": "click" },
        {
          "anchor": "pagetab.users",
          "page": "org.organisation",
          "await": true,
          "advance": "click"
        },
        { "anchor": "org.add-user", "page": "org.organisation", "await": true, "advance": "click" },
        { "anchor": "dialog.card", "page": "org.organisation", "await": true },
        { "anchor": "dialog.primary", "page": "org.organisation" }
      ]
    },
    "academy.credits-and-plans": {
      "purpose": "Academy click path: plan and credit usage.",
      "steps": [
        { "anchor": "layout.user-menu", "advance": "click" },
        { "anchor": "usermenu.org.products", "await": true, "advance": "click" },
        { "anchor": "org.plans", "page": "org.products", "await": true },
        { "anchor": "nav.org.usage", "page": "org.products", "advance": "click" },
        { "anchor": "page.header", "page": "org.usage", "await": true }
      ]
    },
    "academy.privacy": {
      "purpose": "Academy click path: AI notice, homework rule and learner profile.",
      "steps": [
        { "anchor": "nav.agents.list", "advance": "click" },
        {
          "anchor": "agentcard.alpha_tutor",
          "page": "agents.list",
          "await": true,
          "advance": "click",
          "alt": ["agentcard.alpha_flexible", "agentcard.alpha_support", "agentcard.alpha_assist", "agentcard.alpha_basic"]
        },
        {
          "anchor": "aisection.guardrails",
          "page": "agent.general",
          "await": true,
          "advance": "click"
        },
        { "anchor": "setting.__bot_ai_hint", "page": "agent.ai-settings", "await": true },
        { "anchor": "setting.prevent_homework_solving", "page": "agent.ai-settings" },
        { "anchor": "agentmenu.learning", "page": "agent.ai-settings", "advance": "click" },
        {
          "anchor": "tab.learner_profile",
          "page": "agent.learning",
          "await": true,
          "advance": "click"
        },
        { "anchor": "setting.learner_profile_enabled", "page": "agent.learning", "await": true }
      ]
    },
    "academy.prompt-craft": {
      "purpose": "Academy click path: write persona and instructions of an agent.",
      "steps": [
        { "anchor": "nav.agents.list", "advance": "click" },
        {
          "anchor": "agentcard.alpha_flexible",
          "page": "agents.list",
          "await": true,
          "advance": "click",
          "alt": ["agentcard.alpha_tutor", "agentcard.alpha_support", "agentcard.alpha_assist"]
        },
        {
          "anchor": "aisection.basis_persona",
          "page": "agent.general",
          "await": true,
          "advance": "click"
        },
        {
          "anchor": "setting.persona_block",
          "page": "agent.ai-settings",
          "await": true,
          "alt": ["setting.system_prompt_instructions"]
        },
        { "anchor": "setting.behavior_block", "page": "agent.ai-settings" },
        { "anchor": "setting.style_block", "page": "agent.ai-settings", "alt": ["setting.tone"] },
        {
          "anchor": "setting.goal_block",
          "page": "agent.ai-settings",
          "alt": ["setting.learning_goals"]
        },
        { "anchor": "setting.boundaries_block", "page": "agent.ai-settings" }
      ]
    },
    "academy.knowledge-curation": {
      "purpose": "Academy click path: keep a folder's content clean.",
      "steps": [
        { "anchor": "nav.knowledge.base", "advance": "click" },
        { "anchor": "knowledge.bucket-list", "page": "knowledge.base", "await": true },
        { "anchor": "knowledge.bucket-content", "page": "knowledge.base", "await": true },
        { "anchor": "pagetab.websites", "page": "knowledge.base", "advance": "click" },
        { "anchor": "pagetab.courses", "page": "knowledge.base", "advance": "click" },
        { "anchor": "pagetab.files", "page": "knowledge.base", "advance": "click" }
      ]
    },
    "academy.agent-learnings": {
      "purpose": "Academy click path: add a learning for your agents.",
      "steps": [
        { "anchor": "layout.nav-agent-setup", "advance": "click" },
        { "anchor": "tab.learnings", "page": "agents.setup", "await": true },
        {
          "anchor": "tableaction.create",
          "page": "agents.setup",
          "await": true,
          "advance": "click"
        },
        { "anchor": "dialog.card", "page": "agents.setup", "await": true },
        { "anchor": "dialog.primary", "page": "agents.setup" }
      ]
    },
    "academy.course-rollout": {
      "purpose": "Academy click path: test phase — filter thumbs-down answers and turn them into learnings.",
      "steps": [
        {
          "anchor": "navgroup.analytics.conversations",
          "advance": "click",
          "skip_if_visible": "nav.analytics.conversations"
        },
        { "anchor": "nav.analytics.conversations", "advance": "click" },
        { "anchor": "analytics.rating-filter", "page": "analytics.conversations", "await": true },
        {
          "anchor": "analytics.conversation-row",
          "page": "analytics.conversations",
          "await": true,
          "advance": "click",
          "alt": ["analytics.conversations"]
        },
        {
          "anchor": "analytics.feedback-reason",
          "page": "analytics.conversations",
          "await": true,
          "alt": ["analytics.conversation-thread", "analytics.conversations"]
        },
        {
          "anchor": "analytics.learn-from",
          "page": "analytics.conversations",
          "await": true,
          "advance": "click",
          "alt": ["analytics.conversation-thread", "analytics.conversations"]
        },
        { "anchor": "dialog.card", "page": "analytics.conversations", "await": true },
        { "anchor": "dialog.primary", "page": "analytics.conversations", "advance": "click" },
        {
          "anchor": "dialog.primary",
          "page": "analytics.conversations",
          "await": true,
          "advance": "click"
        },
        { "anchor": "dialog.primary", "page": "analytics.conversations", "await": true }
      ]
    },
    "academy.creator-overview": {
      "purpose": "Academy click path: what the Creator offers and where its content lives.",
      "steps": [
        { "anchor": "nav.content.creator", "advance": "click" },
        { "anchor": "creator.new-course", "page": "content.creator", "await": true },
        { "anchor": "creator.new-exercise", "page": "content.creator" },
        { "anchor": "creator.import", "page": "content.creator" },
        { "anchor": "creator.library", "page": "content.creator", "advance": "click" },
        { "anchor": "creator.library-filter", "page": "content.creator-library", "await": true },
        { "anchor": "creator.library-search", "page": "content.creator-library" }
      ]
    },
    "academy.creator-import": {
      "purpose": "Academy click path: import an Articulate Rise package or a document as a course.",
      "steps": [
        { "anchor": "nav.content.creator", "advance": "click" },
        { "anchor": "creator.import", "page": "content.creator", "await": true },
        { "anchor": "dialog.card", "page": "content.creator", "await": true },
        { "anchor": "dialog.primary", "page": "content.creator", "await": true }
      ]
    },
    "academy.creator-design": {
      "purpose": "Academy click path: create an e-learning by hand and open it in the course editor.",
      "steps": [
        { "anchor": "nav.content.creator", "advance": "click" },
        {
          "anchor": "creator.new-course",
          "page": "content.creator",
          "await": true,
          "advance": "click"
        },
        {
          "anchor": "creator.format-elearning",
          "page": "content.creator",
          "await": true,
          "advance": "click"
        },
        {
          "anchor": "creator.by-hand",
          "page": "content.creator",
          "await": true,
          "advance": "click"
        },
        { "anchor": "dialog.card", "page": "content.creator", "await": true },
        { "anchor": "dialog.primary", "page": "content.creator" }
      ]
    },
    "academy.creator-review": {
      "purpose": "Academy click path: where review comments arrive and how to open a course for review.",
      "steps": [
        { "anchor": "nav.content.creator", "advance": "click" },
        { "anchor": "layout.notifications", "page": "content.creator", "await": true },
        { "anchor": "creator.library", "page": "content.creator", "advance": "click" },
        {
          "anchor": "creator.library-open",
          "page": "content.creator-library",
          "await": true,
          "advance": "click",
          "alt": ["creator.library-filter"]
        }
      ]
    }
  }
}
